/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React from 'react'
import {useTranslation} from 'react-i18next'
import {useAppDispatch} from 'helpers/store'
import {closeModal} from 'helpers/actions'
import UiFormModalShell from 'components/Forms/UiFormModalShell'
import UiFormInner from 'components/Forms/UIFormInner'
import UiFormGrid from 'components/Forms/UiFormGrid'
import UiFormActionsBar from 'components/Forms/UiFormActionsBar'
import {PackagesCartItem} from 'types/entities/PackagesCartItem'
import {PackagesCart} from 'types/entities/PackagesCart'
import UiTextarea from 'components/Ui/UiTextarea'
import BookingAction from 'store/booking/BookingAction'
import {toast} from 'utils/toastUtils'
import {useForm} from 'react-hook-form'
import {zodResolver} from '@hookform/resolvers/zod'
import {translate} from 'utils/utility'
import {DocumentTextIcon} from '@heroicons/react/24/outline'
import {BookingSellerPackageCartItemCustomTextInputs, BookingSellerPackageCartItemCustomTextSchema} from 'pages/Front/Booking/Seller/schemas/bookingSellerPackageCartItemCustomTextSchema'

interface EditPackageCartItemModalProps {
    packagesCart: PackagesCart
    item: PackagesCartItem
    onSuccess?: () => void
}

const EditPackageCartItemModal: React.FC<EditPackageCartItemModalProps> = ({
    packagesCart,
    item,
    onSuccess,
}) => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const {
        register,
        handleSubmit,
        setValue,
        formState: { isSubmitting },
    } = useForm<BookingSellerPackageCartItemCustomTextInputs>({
        resolver: zodResolver(BookingSellerPackageCartItemCustomTextSchema),
        defaultValues: {
            custom_text: item.custom_text || '',
        },
    })

    const fillFromDescription = () => {
        const description = item.packageItem?.description
            ? translate(item.packageItem.description)
            : ''
        if (description) {
            setValue('custom_text', description)
        }
    }

    const onSubmit = async (
        data: BookingSellerPackageCartItemCustomTextInputs,
        closeAfterSave: boolean = true
    ) => {
        try {
            await dispatch(
                BookingAction.fetchUpdatePackageCartItem(
                    packagesCart,
                    item.id,
                    {...data, custom_text: data.custom_text.trim()}
                )
            )
            toast.success(t('Item updated'))
            if (closeAfterSave) {
                dispatch(closeModal())
            }
            if (onSuccess) onSuccess()
        } catch (e) {
            console.error(e)
            toast.error(t('Failed to update item'))
        }
    }

    return (
        <UiFormModalShell className="EditPackageCartItemModal" title={t('Edit Item')}>
            <form
                onSubmit={handleSubmit((data) => onSubmit(data, true))}
                className="h-full flex flex-col"
            >
                <div className="p-4 flex-1">
                    <UiFormInner
                        title={t('Custom Text')}
                        actions={
                            item.packageItem?.description ? (
                                <button
                                    type="button"
                                    title={t('Fill from item description') as string}
                                    onClick={fillFromDescription}
                                    className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 rounded hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors"
                                >
                                    <DocumentTextIcon className="w-3.5 h-3.5" />
                                    {t('Fill from description')}
                                </button>
                            ) : undefined
                        }
                    >
                        <UiFormGrid cols={1}>
                            <UiTextarea
                                {...register('custom_text')}
                                placeholder={t('Enter custom text...')}
                                rows={10}
                            />
                        </UiFormGrid>
                    </UiFormInner>
                </div>
                <UiFormActionsBar
                    primaryText={t('Save')}
                    onPrimary={handleSubmit((data) => onSubmit(data, false))}
                    saveAndCloseText={t('Save and close')}
                    onSaveAndClose={handleSubmit((data) =>
                        onSubmit(data, true)
                    )}
                    loading={isSubmitting}
                />
            </form>
        </UiFormModalShell>
    )
}

export default EditPackageCartItemModal
